import './host'

import { UserService } from '@graft/nuget-gc.webportals.users'

import { getSessionToken } from '@common/helpers'
import { isVisionLocal } from '@common/utils/isVisionLocal'
import { readGraftObject } from '@common/utils/readGraftObject'

import type { Invitation } from '@modules/memberships/memberships.type'

import { userToObjectFromToken } from './objects'
import type { User, UserRegionalIdentity } from './user.interface'

export async function getCurrentUser(): Promise<User | null> {
  try {
    if (isVisionLocal()) return null

    const token = await getSessionToken()
    return userToObjectFromToken(token)
  } catch (error) {
    console.error('Failed to get current user:', error)
    return null
  }
}

export async function isUserRegistered(): Promise<boolean> {
  try {
    if (isVisionLocal()) return false

    const token = await getSessionToken()
    return UserService.isUserRegistered(token)
  } catch (error) {
    console.error('Failed to check if user is registered:', error)
    return false
  }
}

export async function updateWorkspaceContext(workspaceId: string): Promise<void> {
  try {
    if (isVisionLocal()) return
    const token = await getSessionToken()
    await UserService.updateWorkspaceContext(token, workspaceId)
  } catch (error) {
    console.error('Failed to update workspace context:', error)
    throw error
  }
}

export async function addUserRegionalIdentity(
  regionString: string,
  lastUsedWorkspaceId = '00000000-0000-0000-0000-000000000000'
): Promise<string | null> {
  try {
    if (isVisionLocal()) return null

    const token = await getSessionToken()
    return await UserService.addUserRegionalIdentity(token, regionString, lastUsedWorkspaceId)
  } catch (error) {
    console.error('Failed to add user regional identity:', error)
    throw error
  }
}

export async function getUserIdentities(): Promise<UserRegionalIdentity[]> {
  try {
    if (isVisionLocal()) return []

    const token = await getSessionToken()
    const graftcodeIdentities = await UserService.getUserIdentities(token)

    return graftcodeIdentities
      ? await Promise.all(graftcodeIdentities.map((identity) => readGraftObject<UserRegionalIdentity>(identity)))
      : []
  } catch (error) {
    console.error('Failed to get user identities:', error)
    return []
  }
}

export async function getUserInvitations(): Promise<Invitation[]> {
  const { getInvitations } = await import('@modules/memberships/memberships')
  return getInvitations()
}

export async function deleteUser(): Promise<void> {
  if (isVisionLocal()) return

  try {
    const token = await getSessionToken()
    await UserService.deleteUser(token)
  } catch (error) {
    console.error('Failed to delete user:', error)
    throw error
  }
}
